import * as React from 'react';
import ReactHtmlParser from 'react-html-parser';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { IProductInfo, ISessionInfo, IState, IUserInfo } from '../CustomTypes';

import {
  apiStatuses,
  FetchProductInfo,
  fetchUpsaleProductInfo,
  loadIdentity,
  logException,
  LogException,
} from '../actions/Actions';
import {
  createLoginSpidLoginLinkWithRedirectUrl,
  forceE24Login,
  getRedirectUrlInLocalStorage,
  getRefererFromLocalStorage,
  getStorageData,
  getUrlForPage,
  safeRedirect,
  toTitleCase,
} from '../fastflowUtils';
import { FamilySharing } from '../input-components/InputComponents';
import {
  addParamToPulseSptCustom,
  trackPulseEngagementEvent,
  trackPulsePageViewEvent,
} from '../PulseUtils';
import { RouteComponentProps } from 'react-router-dom';
import * as queryString from 'query-string';
import {
  BackgroundColor,
  Button,
  ButtonSkin,
  classNames,
  Display,
  FontWeight,
  Heading,
  HeadingLevel,
  IconName,
  Loader,
  Margin,
  Padding,
  TextAlign,
  TextColor,
  Width,
} from '@snoam/pinata';
import { PageHeading } from '../layout-components/PageHeading';
import { transformHtmlFactory } from './HtmlParser';
import TopIcon from '../layout-components/TopIcon';
import { ClientShortHandName } from '@snoam/mono-scc';
import EmbedHtml from './EmbedHtml';

export interface IReceiptPageParamProps {
  productInfo: IProductInfo;
  sessionInfo: ISessionInfo;
  userInfo: IUserInfo;
  upsaleProductInfo: IProductInfo;
  vevContent: string | null;
}

export interface IReceiptPageActionProps {
  logException: LogException;
  fetchUpsaleProductInfo: FetchProductInfo;
}

export interface IQueryParams {
  duplicate?: string;
  spidId?: string;
  redirectUri?: string;
  subscriptionId?: string;
  paymentMethodId?: string;
  sig?: string;
  referer?: string;
}

export interface IReceiptPageProps
  extends IReceiptPageParamProps,
    IReceiptPageActionProps,
    RouteComponentProps<Record<string, never>, Record<string, never>> {}
export interface IReceiptPageState {
  loading: boolean;
}

const styleClass = {
  boldText: FontWeight.FONT_BOLD,
  button: classNames(Width.W_FULL, Margin.MY_10),
  container: classNames(
    TextAlign.TEXT_CENTER,
    FontWeight.FONT_LIGHT,
    Padding.PT_4,
    TextColor.TEXT_NEUTRAL_7,
    Padding.MD_PT_12,
  ),
  productImage: classNames(Width.W_1_2, Margin.MY_6, Margin.MX_AUTO),
  receipt: classNames(BackgroundColor.BG_POSITIVE),
};

export class ReceiptPage extends React.Component<
  IReceiptPageProps,
  IReceiptPageState
> {
  public state: IReceiptPageState = {
    loading: false,
  };
  constructor(props: IReceiptPageProps) {
    super(props);
    this.receiptPageOrRedirectAsUrl =
      this.receiptPageOrRedirectAsUrl.bind(this);
    this.goToReceiptPageOrRedirect = this.goToReceiptPageOrRedirect.bind(this);
    this.goToSpidLogin = this.goToSpidLogin.bind(this);
    this.getTrackingClickEventName = this.getTrackingClickEventName.bind(this);
    this.buyUpsaleProduct = this.buyUpsaleProduct.bind(this);
    this.showUpsaleBox = this.showUpsaleBox.bind(this);
    this.replaceProperties = this.replaceProperties.bind(this);
  }

  public componentDidMount() {
    const query: IQueryParams = queryString.parse(
      this.props.location.search,
    ) as IQueryParams;
    this.setState({ loading: true });
    setTimeout(() => {
      this.setState({ loading: false });
    }, 3000);
    loadIdentity()
      .then((identity) => identity.hasSession())
      .catch(() => Promise.resolve())
      .then(() => {
        const duplicate = query.duplicate === 'true';
        const userInfo: Pick<IUserInfo, 'hasProduct' | 'spidId'> =
          Object.assign({}, this.props.userInfo, {
            spidId: (this.props.userInfo || {}).spidId || query.spidId,
          });
        if (duplicate) {
          addParamToPulseSptCustom('abo.ff.quarantinedDuplicate', 'duplicate');
          trackPulsePageViewEvent(userInfo, 'Duplicate');
        } else {
          if (!this.props.vevContent) {
            trackPulsePageViewEvent(userInfo, 'receipt');
          }
        }
      });

    if (this.showUpsaleBox()) {
      this.props.fetchUpsaleProductInfo(
        this.props.productInfo.upsaleSubscriptionConfigurationId,
      );
    }
  }

  public componentDidUpdate(
    prevProps: Readonly<IReceiptPageProps>,
    prevState: Readonly<IReceiptPageState>, // eslint-disable-line @typescript-eslint/no-unused-vars
    prevContext: any, // eslint-disable-line @typescript-eslint/no-unused-vars
  ): void {
    if (this.props.userInfo.firstName !== prevProps.userInfo.firstName) {
      this.setState({ loading: false });
    }
  }

  public isLoggedInTracking = () => {
    const loggedIn: boolean =
      this.props.sessionInfo.apiStatus === apiStatuses.SUCCESS;
    if (loggedIn) {
      return {
        loginstate: 'continue to newspaper',
      };
    }
    return {
      loginstate: 'continue to login',
    };
  };

  public render() {
    const loggedIn: boolean =
      this.props.sessionInfo.apiStatus === apiStatuses.SUCCESS;
    const { firstName, spidPrimaryEmail } = this.props.userInfo;
    const {
      header,
      owningClientName,
      goDirectlyToReceiptPage,
      receiptPageAboveButton,
      receiptPageButton,
      receiptPageNotLoggedInButton,
      showFamilySharing,
      owningClient,
    } = this.props.productInfo;
    const receiptPageCenter = this.replaceProperties(
      this.props.productInfo.receiptPageCenter,
    );
    const receiptPageBottom = this.replaceProperties(
      this.props.productInfo.receiptPageBottom,
    );
    const firstNameCorrectCase = toTitleCase(firstName);

    const query: IQueryParams = queryString.parse(
      this.props.location.search,
    ) as IQueryParams;

    let productName = 'artikkel';
    if (!query.redirectUri) {
      productName = owningClientName;
    }

    // This is used to redirect user to Schibsted Account if the redirectUri contains e24.no and owningClient is not e24.
    const e24Login = forceE24Login(
      owningClient,
      getRedirectUrlInLocalStorage() || query.redirectUri,
    );

    const duplicate = query.duplicate || false;
    const goToReceiptPageOrRedirect: boolean =
      loggedIn || goDirectlyToReceiptPage;

    const formattedRecieptPage = ReactHtmlParser(receiptPageCenter, {
      transform: transformHtmlFactory({
        form: classNames(TextAlign.TEXT_CENTER, Padding.P_4),
        li: classNames(TextAlign.TEXT_LEFT, Display.FLEX),
      }),
    });

    const formattedReceiptPageButton = ReactHtmlParser(
      receiptPageButton,
      {
        transform: transformHtmlFactory(),
      },
      true,
    );

    const formattedReceiptPageNotLoggedInButton = ReactHtmlParser(
      receiptPageNotLoggedInButton,
      {
        transform: transformHtmlFactory(),
      },
      true,
    );

    const formattedReceiptpageAboveButton = ReactHtmlParser(
      receiptPageAboveButton,
      {
        transform: transformHtmlFactory(),
      },
      true,
    );

    const formattedReceiptPageBottom = ReactHtmlParser(receiptPageBottom, {
      transform: transformHtmlFactory(),
    });

    const greetingText = (
      <PageHeading level={HeadingLevel.ONE} className={Margin.MB_4}>
        Hei, {firstNameCorrectCase}
      </PageHeading>
    );

    const successText = (
      <>
        <Heading level={HeadingLevel.FOUR}>
          Ditt abonnement på {header} er nå opprettet. Vi håper du blir fornøyd!
        </Heading>
        {receiptPageCenter && formattedRecieptPage}
      </>
    );

    const duplicateText = (
      <Heading level={HeadingLevel.FOUR}>
        Du har allerede et abonnement hos oss, og trenger derfor ikke å bestille
        på nytt.
      </Heading>
    );

    const { channel } = getRefererFromLocalStorage();
    const continueButton = (
      <Button
        onClick={this.goToReceiptPageOrRedirect}
        skin={ButtonSkin.PRIMARY}
        className={styleClass.button}
        text={
          receiptPageButton
            ? formattedReceiptPageButton
            : channel.includes('article')
              ? `Tilbake til artikkel`
              : `Fortsett til ${productName}`
        }
      />
    );

    const logInToReadButton = (
      <Button
        onClick={this.goToSpidLogin}
        skin={ButtonSkin.PRIMARY}
        text={
          receiptPageNotLoggedInButton
            ? formattedReceiptPageNotLoggedInButton
            : channel.includes('article')
              ? `Tilbake til artikkel`
              : `Logg inn for å lese ${productName}`
        }
      />
    );

    const upsaleBox = (
      <>
        <p>{this.props.productInfo.upsaleHeading}</p>
        <img
          alt={'Produktbilde'}
          className={styleClass.productImage}
          src={this.props.upsaleProductInfo.imageurl}
        />
        <Button
          onClick={this.buyUpsaleProduct}
          skin={ButtonSkin.PRIMARY}
          text={this.props.upsaleProductInfo.orderButtonText}
        />
      </>
    );

    const registeredEmail = (
      <p>
        {'Du er registrert med e-post '}
        <span className={styleClass.boldText}>{spidPrimaryEmail}</span>.
      </p>
    );

    if (this.state.loading) {
      return (
        <div className={styleClass.container}>
          <Loader />
        </div>
      );
    }

    if (this.props.vevContent) {
      return (
        <EmbedHtml
          html={this.props.vevContent}
          query={this.props.location.search}
          userInfo={this.props.userInfo}
        />
      );
    }

    return (
      <div className={styleClass.container} data-testing={'receipt-page'}>
        <TopIcon iconName={IconName.CHECK_CIRCLE_OUTLINE} />
        {greetingText}

        {duplicate ? duplicateText : successText}

        {!duplicate && showFamilySharing && (
          <FamilySharing newspaperName={owningClientName} />
        )}

        {receiptPageAboveButton && (
          <Button
            skin={ButtonSkin.PRIMARY}
            text={formattedReceiptpageAboveButton}
          />
        )}
        {e24Login
          ? logInToReadButton
          : goToReceiptPageOrRedirect
            ? continueButton
            : logInToReadButton}
        {spidPrimaryEmail && registeredEmail}
        {this.showUpsaleBox() && upsaleBox}
        {receiptPageBottom && formattedReceiptPageBottom}
      </div>
    );
  }

  private showUpsaleBox(): boolean {
    const query: IQueryParams = queryString.parse(
      this.props.location.search,
    ) as IQueryParams;
    return !!(
      this.props.productInfo.upsaleSubscriptionConfigurationId &&
      query.subscriptionId
    );
  }

  private goToReceiptPageOrRedirect() {
    trackPulseEngagementEvent(
      this.props.userInfo,
      this.getTrackingClickEventName(),
    );
    const redirectUri = this.receiptPageOrRedirectAsUrl();
    (window as any).location.href = safeRedirect(redirectUri);
  }

  private buyUpsaleProduct() {
    const query: IQueryParams = queryString.parse(
      this.props.location.search,
    ) as IQueryParams;
    trackPulseEngagementEvent(this.props.userInfo, 'click upsale product buy');
    const {
      paymentMethodId,
      spidId,
      sig,
      subscriptionId,
      redirectUri,
      referer,
    } = query;
    const fullUrlForStartPage = getUrlForPage(
      this.props.productInfo.upsaleSubscriptionConfigurationId,
      'start',
    );
    (window as any).location =
      `${fullUrlForStartPage}?paymentMethodId=${paymentMethodId}&purchasedSubscriptionId=${subscriptionId}&referer=${encodeURIComponent(
        referer,
      )}&redirectUri=${encodeURIComponent(
        redirectUri,
      )}&spidId=${spidId}&sig=${sig}`;
  }

  private goToSpidLogin() {
    const query: IQueryParams = queryString.parse(
      this.props.location.search,
    ) as IQueryParams;
    const { redirectUri } = query;
    const uri = redirectUri ? redirectUri : '';

    trackPulseEngagementEvent(
      this.props.userInfo,
      this.getTrackingClickEventName(),
    );

    const { owningClient, spidTeaser } = this.props.productInfo;
    let loginClient = owningClient;
    if (forceE24Login(owningClient, getRedirectUrlInLocalStorage() || uri)) {
      loginClient = ClientShortHandName.E24;
    }

    window.location.replace(
      createLoginSpidLoginLinkWithRedirectUrl(
        loginClient,
        this.props.userInfo.spidPrimaryEmail,
        this.receiptPageOrRedirectAsUrl(),
        spidTeaser,
      ),
    );
  }

  private receiptPageOrRedirectAsUrl() {
    const {
      productInfo: { receiptPageButtonUrl, extraAllowedRedirectDomains },
    } = this.props;
    const query: IQueryParams = queryString.parse(
      this.props.location.search,
    ) as IQueryParams;
    const { redirectUri } = query;
    if (receiptPageButtonUrl) {
      return receiptPageButtonUrl;
    }
    if (redirectUri) {
      return decodeURIComponent(redirectUri);
    }

    const redirectDataFromLocalStorage = getStorageData();
    if (
      redirectDataFromLocalStorage &&
      redirectDataFromLocalStorage.redirectUri
    ) {
      const frontPageUrl = this.props.productInfo.myPageUrl
        .replace('minside', '')
        .replace('kundeportal', 'www');
      const domain = frontPageUrl.split('.')[1];

      const redirectLeadsToArticle =
        !!(extraAllowedRedirectDomains || []).find((d) =>
          redirectDataFromLocalStorage.redirectUri.includes(d),
        ) ||
        redirectDataFromLocalStorage.redirectUri.includes(domain) ||
        redirectDataFromLocalStorage.redirectUri.includes('e24') ||
        redirectDataFromLocalStorage.redirectUri.includes('dinepenger');
      if (!redirectLeadsToArticle) {
        return safeRedirect(frontPageUrl);
      } else {
        return redirectDataFromLocalStorage.redirectUri;
      }
    }

    const loggedIn = this.props.sessionInfo.apiStatus === apiStatuses.SUCCESS;
    const { subscriptionTypeId, subscriptionConfigurationId, owningClient } =
      this.props.productInfo;
    return `${location.protocol}//${location.host}/bestilling/receiptpage.htms?subscriptionTypeId=${subscriptionTypeId}&_skjemaId=${subscriptionConfigurationId}&client=${owningClient}&loggedIn=${loggedIn}`;
  }

  private getTrackingClickEventName() {
    const query: IQueryParams = queryString.parse(
      this.props.location.search,
    ) as IQueryParams;
    return query.duplicate === 'true'
      ? 'click duplicate'
      : 'click order confirmation';
  }

  private replaceProperties(text: string) {
    const { firstName, lastName, spidPrimaryEmail, spidId } =
      this.props.userInfo;
    return (
      text &&
      text
        .replace('${spidId}', '' + spidId)
        .replace('${email}', '' + spidPrimaryEmail)
        .replace('${firstName}', '' + firstName)
        .replace('${lastName}', '' + lastName)
    );
  }
}

export default connect<
  Pick<
    IReceiptPageParamProps,
    'productInfo' | 'sessionInfo' | 'upsaleProductInfo' | 'userInfo'
  >,
  IReceiptPageActionProps,
  Pick<IReceiptPageParamProps, 'vevContent'>
>(
  (state: IState) =>
    ({
      productInfo: state.productInfo,
      sessionInfo: state.sessionInfo,
      upsaleProductInfo: state.upsaleProductInfo,
      userInfo: state.userInfo,
    }) as IReceiptPageParamProps,
  (dispatch) =>
    bindActionCreators(
      { logException, fetchUpsaleProductInfo },
      dispatch,
    ) as IReceiptPageActionProps,
)(ReceiptPage);
